import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { TrendingUp, ShoppingBag, Store, Award } from 'lucide-react';

export default function Dashboard() {
  const { user, logout } = useAuth();

  const { data: overview } = useQuery({
    queryKey: ['admin-overview'],
    queryFn: () => adminAPI.getOverview().then((res) => res.data),
  });

  const stats = [
    {
      label: 'Total Offers',
      value: overview?.data?.totalOffers || 0,
      icon: ShoppingBag,
      color: 'bg-blue-500',
    },
    {
      label: 'Active Offers',
      value: overview?.data?.activeOffers || 0,
      icon: TrendingUp,
      color: 'bg-green-500',
    },
    {
      label: 'Total Restaurants',
      value: overview?.data?.totalRestaurants || 0,
      icon: Store,
      color: 'bg-purple-500',
    },
    {
      label: 'Premium Restaurants',
      value: overview?.data?.premiumRestaurants || 0,
      icon: Award,
      color: 'bg-yellow-500',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <div className="flex items-center gap-4">
              <span className="text-gray-700">{user?.email}</span>
              <button
                onClick={logout}
                className="px-4 py-2 text-red-600 hover:text-red-700 font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat) => (
            <div key={stat.label} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <span className="text-3xl font-bold text-gray-900">{stat.value}</span>
              </div>
              <p className="text-gray-600">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/admin/offers"
              className="p-6 border-2 border-gray-200 rounded-lg hover:border-red-600 hover:bg-red-50 transition text-center"
            >
              <ShoppingBag className="w-8 h-8 mx-auto mb-2 text-red-600" />
              <h3 className="font-semibold text-gray-900 mb-1">Manage Offers</h3>
              <p className="text-sm text-gray-600">Create, edit, and delete offers</p>
            </Link>
            <Link
              to="/admin/restaurants"
              className="p-6 border-2 border-gray-200 rounded-lg hover:border-red-600 hover:bg-red-50 transition text-center"
            >
              <Store className="w-8 h-8 mx-auto mb-2 text-red-600" />
              <h3 className="font-semibold text-gray-900 mb-1">Manage Restaurants</h3>
              <p className="text-sm text-gray-600">Add and update restaurant info</p>
            </Link>
            <Link
              to="/"
              target="_blank"
              className="p-6 border-2 border-gray-200 rounded-lg hover:border-red-600 hover:bg-red-50 transition text-center"
            >
              <TrendingUp className="w-8 h-8 mx-auto mb-2 text-red-600" />
              <h3 className="font-semibold text-gray-900 mb-1">View Public Site</h3>
              <p className="text-sm text-gray-600">See what users see</p>
            </Link>
          </div>
        </div>

        {/* Welcome Message */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg shadow p-8 text-center">
          <h2 className="text-2xl font-bold mb-2">Welcome to UdaipurDeals Admin</h2>
          <p className="text-red-100">
            Manage restaurant offers and grow Udaipur's food community
          </p>
        </div>
      </div>
    </div>
  );
}
